import { ActivityLogEntry, ActivityLogItemStatus } from 'components/UI/V3/ActivityLog/types';
import { ActivityLogEvent } from '@spike/membership-model';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { faArrowRight, faPause, faPlay, faCalendarPen, faCalendarXmark } from '@fortawesome/pro-solid-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import moment from 'moment';
import React from 'react';
import getCardIcon from 'hooks/useCardIcon';

const getStatusFromEventType = (eventType: string): ActivityLogItemStatus => {
    if (
        eventType.includes('successful') ||
        eventType.includes('purchased') ||
        eventType.includes('renewed') ||
        eventType === 'item_used' ||
        eventType === 'refund_processed'
    ) {
        return 'success';
    }
    if (eventType.includes('failed')) {
        return 'warning';
    }
    if (eventType.includes('canceled')) {
        return 'canceled';
    }
    if (
        eventType.includes('paused') ||
        eventType.includes('resumed') ||
        eventType === 'credit_card_updated' ||
        eventType === 'payment_link'
    ) {
        return 'info';
    }
    return 'info';
};

const getIconFromEventType = (eventType: string): IconProp | undefined => {
    switch (eventType) {
        case 'enrollment_paused':
            return faPause;
        case 'enrollment_resumed':
            return faPlay;
        case 'end_date_edited':
            return faCalendarPen;
        case 'end_date_removed':
            return faCalendarXmark;
        case 'membership_canceled':
            return faXmark;
        case 'payment_link':
            return faArrowRight;
        default:
            return undefined;
    }
};

const formatMessage = (event: ActivityLogEvent): React.ReactNode => {
    const { actor, event_type, metadata } = event;

    const formattedAmount = metadata.amount ? `$${parseFloat(metadata.amount).toFixed(2)}` : null;

    const cardDisplay: React.ReactNode = (() => {
        if (metadata.card_brand === 'cash') return <strong>Cash/Other</strong>;
        if (metadata.card_brand && metadata.card_last4) {
            return (
                <>
                    <img
                        src={getCardIcon(metadata.card_brand)}
                        alt={metadata.card_brand}
                        style={{
                            width: 24,
                            height: 'auto',
                            display: 'inline-block',
                            verticalAlign: 'middle',
                            margin: '0 4px'
                        }}
                    />{' '}
                    <strong>{metadata.card_last4}</strong>
                </>
            );
        }
        return null;
    })();

    switch (event_type) {
        case 'charge_successful':
            return (
                <>
                    Successfully charged <strong>{formattedAmount}</strong>
                    {cardDisplay && <> to {cardDisplay}</>}
                </>
            );

        case 'refund_processed':
            return (
                <>
                    Successfully refunded <strong>{formattedAmount}</strong>
                    {cardDisplay && <> to {cardDisplay}</>}
                </>
            );

        case 'charge_failed':
            return (
                <>
                    Failed to charge <strong>{formattedAmount}</strong>
                    {cardDisplay && <> to {cardDisplay}</>}
                </>
            );

        case 'item_used':
            return (
                <>
                    <strong>{event.actor || metadata.staff_name}</strong> redeemed <strong>{metadata.item_name}</strong>
                    {metadata.pet_name && (
                        <>
                            {' '}
                            for <strong>{metadata.pet_name}</strong>
                        </>
                    )}
                    {metadata.room_name && (
                        <>
                            {' '}
                            — Room: <strong>{metadata.room_name}</strong>
                        </>
                    )}
                </>
            );

        case 'credit_card_updated':
            return <>Credit card updated</>;

        case 'membership_purchased':
            return <strong>Membership purchased</strong>;

        case 'membership_paused':
            return (
                <>
                    <strong>Membership paused</strong>
                    {actor && (
                        <>
                            {' '}
                            by <strong>{actor}</strong>
                        </>
                    )}
                </>
            );

        case 'membership_resumed':
            return (
                <>
                    <strong>Membership resumed</strong>
                    {actor && (
                        <>
                            {' '}
                            by <strong>{actor}</strong>
                        </>
                    )}
                </>
            );

        case 'subscription_renewed':
            return <strong>Subscription renewed</strong>;

        case 'membership_canceled':
            return <strong>Membership canceled</strong>;

        case 'payment_link':
            return <>Pay by Link sent for membership payment</>;

        default:
            return event.description;
    }
};

interface FormatActivityLogOptions {
    onOpenBooking?: (bookingId: number, orderId?: number) => void;
}

export const formatActivityLog = (
    events: ActivityLogEvent[],
    { onOpenBooking }: FormatActivityLogOptions = {}
): ActivityLogEntry[] => {
    return events.map((event, index) => {
        const metadata = event.metadata;
        const bookingId = event.event_type === 'item_used' ? metadata?.booking_id : undefined;

        return {
            id: `${event.event_type}-${event.occurred_at}-${index}`,
            message: formatMessage(event),
            date: moment(event.occurred_at),
            status: getStatusFromEventType(event.event_type),
            icon: getIconFromEventType(event.event_type),
            onClick: bookingId && onOpenBooking ? () => onOpenBooking(bookingId, metadata?.order_id) : undefined
        };
    });
};
